import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const PaymentResult = ({ cancelled }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled) return;
    const sid = params.get("session_id");
    if (!sid) { setState("cancelled"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await getPaymentStatus(sid);
        if (r.payment_status === "paid") { setState("success"); return; }
        if (r.status === "expired" || r.payment_status === "failed") { setState("cancelled"); return; }
      } catch { /* retry */ }
      if (tries < 10) setTimeout(poll, 1500);
      else setState("cancelled");
    };
    poll();
  }, [cancelled, params]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <Card className="max-w-md p-10 text-center" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
            <p className="mt-6 text-lg font-semibold text-slate-900">{t("payment.checking")}</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50"><CheckCircle2 className="h-9 w-9 text-emerald-600" /></div>
            <h1 className="font-display mt-6 text-2xl font-bold text-slate-900">{t("payment.success")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("payment.successMsg")}</p>
          </>
        )}
        {state === "cancelled" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-50"><XCircle className="h-9 w-9 text-rose-600" /></div>
            <h1 className="font-display mt-6 text-2xl font-bold text-slate-900">{t("payment.cancel")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("payment.cancelMsg")}</p>
          </>
        )}
        {state !== "checking" && (
          <Button className="mt-8" onClick={() => navigate("/invoices")} data-testid="back-invoices-button">{t("payment.backInvoices")}</Button>
        )}
      </Card>
    </div>
  );
};

export default PaymentResult;
